import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { isEqual } from 'lodash';
import EditableTextAreaInput from './EditableTextAreaInput';
import { ZodEffects, ZodObject, ZodString } from 'zod';
import { zodFieldErrors } from '../../utils/zod';
import EditableTextAreaBasic from './EditableTextAreaBasic';

interface IProps {
  placeholder: string;
  value: string;
  validation: ZodObject<{ value: ZodEffects<ZodString, string, string> }>;
  defaultRows?: number;
  isBold?: boolean;
  isRenamed?: boolean;
  onChange: (newVal: string) => void;
  startEditable?: boolean;
}

const EditingViewContainer = styled.div`
  max-width: 100%;
`;

const EditableTextArea = ({
  placeholder,
  value,
  defaultRows,
  validation,
  isRenamed = false,
  isBold = false,
  onChange,
  startEditable,
}: IProps) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [isEditing, setIsEditing] = useState(startEditable ?? false);
  const [draftValue, setDraftValue] = useState(value);

  const safeParse = validation.safeParse({
    value: draftValue,
  });
  const isFormValid = safeParse.success;
  const errors = !safeParse.success
    ? zodFieldErrors(safeParse.error, 'value')
    : undefined;

  const onSubmit = () => {
    if (safeParse.success) {
      if (!isEqual(draftValue, value)) {
        onChange(draftValue);
      }
      setIsEditing(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const hasScrollBar =
    (ref.current?.clientHeight ?? 0) < (ref.current?.scrollHeight ?? 0);

  if (isEditing) {
    return (
      <EditingViewContainer>
        <EditableTextAreaInput
          ref={ref}
          autoFocus={true}
          defaultRows={defaultRows}
          value={draftValue}
          onChange={setDraftValue}
          placeholder={placeholder}
          onSubmit={onSubmit}
          onBlur={onSubmit}
          hasError={!isFormValid}
          errorMessage={errors}
          hasVerticalScroll={hasScrollBar}
        />
      </EditingViewContainer>
    );
  }

  return (
    <EditableTextAreaBasic
      onClick={handleEdit}
      value={value}
      placeholder={placeholder}
      isBold={isBold}
      isRenamed={isRenamed}
    />
  );
};

export default EditableTextArea;
